import { css } from '@emotion/core'
import isPropValid from '@emotion/is-prop-valid'
import styled from '@emotion/styled'
import { transparentize } from 'polished'
import { Link } from 'react-router'

import { BorderRadius } from '../../../../enums/borderRadius'
import { Color } from '../../../../enums/color'
import { Spacing } from '../../../../enums/spacing'
import { pxToNumber } from '../../../../utils/pxToNumber'
import { sizeToDimension } from '../../../Button'
import { TABLE_ROW_HOVER_COLOR } from '../../constants/tableColors'
import { TABLE_ROW_HORIZONTAL_SPACING, TABLE_ROW_VERTICAL_SPACING } from '../../enums/styles'
import { getRowPadding } from '../../utils/getRowPadding'
import { TableCellWrapper } from '../TableCell/styles'

export const STATIC_ACTIONS_SPACING_LEFT = Spacing.S8
export const STATIC_ACTIONS_SPACING_RIGHT = `-${pxToNumber(TABLE_ROW_HORIZONTAL_SPACING) / 2}px`
const SECTION_HEADER_MIN_HEIGHT = '36px'

interface TableRowWrapperProps {
  clickable?: boolean
  disabled?: boolean
  /* When false, pointer events are not disabled even when row is disabled (used with disabledRowsClickable). */
  disablePointerEvents?: boolean
  expandable?: boolean
  expanded?: boolean
  hoverable?: boolean
  ignoreSpacing?: boolean
  isSectionHeader?: boolean
  isSkeletonView?: boolean
  selectable: boolean
  withStaticActionOffset?: boolean
}

export const TableRowWrapper = styled.li<TableRowWrapperProps>`
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  align-items: center;
  min-height: 56px;
  border-top: 1px solid ${Color.Grey100};
  position: relative;
  color: ${Color.Grey950};
  cursor: ${({ clickable, expandable }) => (clickable || expandable ? 'pointer' : 'initial')};
  ${({ selectable, expandable = false, ignoreSpacing = false, withStaticActionOffset = false }) =>
    getRowPadding({ ignoreSpacing, selectable, expandable, withStaticActionOffset })};

  ${({ isSectionHeader }) =>
    isSectionHeader &&
    `min-height: ${SECTION_HEADER_MIN_HEIGHT};
    padding-block: ${Spacing.S8};
    background-color: ${Color.Grey50};
    `}
  ${({ disabled }) => disabled && `opacity: 0.5;`}
  ${({ disablePointerEvents, disabled }) =>
    (disablePointerEvents ?? disabled) &&
    `
      pointer-events: none;
  `}
  ${({ clickable, expandable, expanded, hoverable }) => {
    if (expanded) {
      return `background-color: ${transparentize(0.3, Color.Grey50)};`
    }

    if (clickable || hoverable || expandable) {
      return `
        &:hover {
          background-color: ${TABLE_ROW_HOVER_COLOR};
        }
      `
    }
  }}
  &:hover {
    // To show cells that are hidden by default (see TableCellWrapper styles)
    // and should reveal on mouse hover

    ${() => TableCellWrapper} {
      opacity: 1;
    }

    ${() => TableHoverActions} {
      opacity: 1;
      pointer-events: all;
    }
  }

  &:first-of-type {
    border-top: 0;
  }

  &:last-of-type {
    border-bottom-right-radius: ${BorderRadius.R12};
    border-bottom-left-radius: ${BorderRadius.R12};
  }
`

const getTableLinkStyles = (
  ignoreSpacing: boolean,
  selectable: boolean,
  expandable: boolean,
  withStaticActionOffset: boolean,
) => `
  display: inherit;
  height: 100%;
  flex: inherit;
  align-items: inherit;
  font: inherit;
  color: currentColor;
  text-decoration: none;
  ${getRowPadding({ ignoreSpacing, selectable, expandable, withStaticActionOffset })};
`

interface TableRowLinkProps {
  selectable: boolean
}

export const TableRowLink = styled.a<TableRowLinkProps>`
  ${({ selectable }) => getTableLinkStyles(false, selectable, false, false)};
`

export const TableRowRouterLink = styled(Link, {
  shouldForwardProp: (propName) => isPropValid(propName),
})<TableRowLinkProps>`
  ${({ selectable }) => getTableLinkStyles(false, selectable, false, false)};
`

interface TableHoverActionsProps {
  bgColor: Color
  withStaticActions?: boolean
}

export const TableHoverActions = styled.aside<TableHoverActionsProps>`
  ${({ bgColor, withStaticActions }) => css`
    display: flex;
    opacity: 0;
    align-items: center;
    height: 100%;
    position: absolute;
    top: 0;
    right: ${withStaticActions
      ? sizeToDimension.sm +
        Math.abs(pxToNumber(STATIC_ACTIONS_SPACING_RIGHT)) +
        Math.abs(pxToNumber(STATIC_ACTIONS_SPACING_LEFT))
      : 0}px;
    background-color: ${bgColor};
    pointer-events: none;
    ${getRowPadding()};
    ${withStaticActions ? 'padding-right: 0' : ''};

    &:before {
      content: '';
      width: 50px;
      height: 100%;
      position: absolute;
      top: 0;
      right: 100%;
      background: linear-gradient(to right, ${transparentize(1, bgColor)} 10%, ${bgColor} 100%);
    }

    /* Not rendering hover state when nothing is inside, layout-wise */

    &:empty {
      background-color: transparent;

      &:before {
        display: none;
      }
    }
  `}
`

export const TableStaticActions = styled.aside`
  display: flex;
  align-items: center;
  margin-top: -${TABLE_ROW_VERTICAL_SPACING};
  margin-right: ${STATIC_ACTIONS_SPACING_RIGHT};
  margin-bottom: -${TABLE_ROW_VERTICAL_SPACING};
  margin-left: ${STATIC_ACTIONS_SPACING_LEFT};
`
